import {
  Button,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { ClearIcon } from "@mui/x-date-pickers";
import { closeIcon } from "assets/images";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { courseListRequest } from "store/course/actions";
import { CourseListSuccessPayload } from "store/course/types";
import {
  CLOSE_TOOLTIP_TITLE,
  DEFAULT_PAGE_NO,
  MAX_INT_VALUE,
} from "utils/constants/constant";
import { OrderByOptions } from "utils/enums/sorting";
import {
  getPagePayloadValues,
  hideLoaderForBatchApiCall,
  onTextFieldKeyPress,
  showLoaderForBatchApiCall,
} from "utils/helper";
import { ICourse, ICoursePagePayloadValues } from "utils/interfaces/course";
import { IStudentWaitListedFilter } from "utils/interfaces/student";

interface IStudentWaitListedFilterProps {
  filterBox: boolean;
  setFilterBox: React.Dispatch<React.SetStateAction<boolean>>;
  handleApplyClick: (formData: IStudentWaitListedFilter) => void;
  filteredData?: IStudentWaitListedFilter;
  pageNo: number;
  setPageNo: React.Dispatch<React.SetStateAction<number>>;
  setFilteredData: React.Dispatch<
    React.SetStateAction<IStudentWaitListedFilter | undefined>
  >;
  getStudentWaitListedList: (
    filterData?: IStudentWaitListedFilter,
    filtered?: boolean,
    pageNum?: number
  ) => void;
  setAppliedFilter: React.Dispatch<React.SetStateAction<boolean>>;
}

const StudentWaitlistedFilter: React.FC<IStudentWaitListedFilterProps> = ({
  filterBox,
  setFilterBox,
  handleApplyClick,
  filteredData,
  pageNo,
  setPageNo,
  setFilteredData,
  getStudentWaitListedList,
  setAppliedFilter,
}) => {
  const [courseList, setCourseList] = useState<ICourse[]>([]);
  const dispatch = useDispatch();

  const { handleSubmit, reset, register, setValue, watch, control } =
    useForm<IStudentWaitListedFilter>({
      defaultValues: {
        courseId: null,
        courseNo: "",
        studentName: "",
      },
    });

  const onCourseListSuccess = (response: CourseListSuccessPayload) => {
    setCourseList(response.courseList);
    hideLoaderForBatchApiCall();
  };

  const getCourseList = () => {
    showLoaderForBatchApiCall();
    const values: ICoursePagePayloadValues = getPagePayloadValues(
      "Name",
      OrderByOptions.Asc,
      DEFAULT_PAGE_NO,
      MAX_INT_VALUE
    );
    values.name = "";
    const payload = {
      values,
      callback: onCourseListSuccess,
    };

    dispatch(courseListRequest(payload));
  };

  useEffect(() => {
    getCourseList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBox]);

  useEffect(() => {
    if (filteredData) {
      setValue("courseNo", filteredData?.courseNo);
      setValue("courseId", filteredData?.courseId);
      setValue("studentName", filteredData?.studentName);
    } else {
      reset();
    }
  }, [filteredData, setValue, filterBox, reset]);

  const onClearClick = () => {
    reset();
    setFilterBox(false);
    setAppliedFilter(false);
    document.body.classList.toggle("filter-open");
    if (pageNo === DEFAULT_PAGE_NO) {
      getStudentWaitListedList();
    } else {
      setPageNo(1);
    }
    setFilteredData(undefined);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleApplyClick)}>
        <Box className="filter-popover">
          <Box className="filter-header">
            <Typography variant="h4">Filter</Typography>
            <Tooltip
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              title={CLOSE_TOOLTIP_TITLE}
              arrow
            >
              <IconButton
                onClick={() => {
                  setFilterBox(false);
                  document.body.classList.toggle("filter-open");
                }}
              >
                <img src={closeIcon} alt="close" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box className="filter-body">
            <TextField
              id="studentName"
              label="Student Name"
              fullWidth
              variant="outlined"
              {...register("studentName")}
              onKeyDown={(event) =>
                onTextFieldKeyPress(
                  event,
                  filteredData ? filteredData?.studentName?.length : 0
                )
              }
            />
            <FormControl fullWidth className="select">
              <InputLabel
                id="course-select-label"
                shrink={watch("courseId") !== null}
              >
                Course Name
              </InputLabel>
              <Controller
                name="courseId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="course-select-label"
                    id="course-select"
                    value={field.value ?? ""}
                    displayEmpty
                    endAdornment={
                      field.value ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("courseId", null);
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                  >
                    {courseList?.map((course) => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <TextField
              id="courseNo"
              label="Course#"
              fullWidth
              variant="outlined"
              {...register("courseNo")}
              onKeyDown={(event) =>
                onTextFieldKeyPress(
                  event,
                  filteredData ? filteredData?.courseNo?.length : 0
                )
              }
            />
          </Box>
          <Box className="filter-footer">
            <Button variant="contained" className="btn-apply" type="submit">
              Apply
            </Button>
            <Button
              variant="outlined"
              className="btn-clear"
              onClick={onClearClick}
              disabled={
                !(
                  watch("courseNo") ||
                  watch("courseId") ||
                  watch("studentName")
                )
              }
            >
              Clear
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default StudentWaitlistedFilter;
